import {
  CustomFieldResourceType,
  CustomFieldType,
  type CustomFieldDefinition,
} from "@repo/app-types/entities/customField";
import {
  buildRevenueOutcomeDefinition,
  isRevenueOutcomeDefinition,
  type OutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import {
  SimpleSelect,
  type SimpleSelectOption,
} from "@repo/ui/Select/simpleSelect";
import { useMemo, type FC } from "react";
import { useAllUserCustomFieldDefinitionsQuery } from "../../api/entities/userCustomField.js";

interface RevenueCustomFieldSelectProps {
  outcome: OutcomeDefinition | null;
  onUpdateOutcome: (outcome: OutcomeDefinition) => void;
  isReadOnly: boolean;
}

export const RevenueCustomFieldSelect: FC<RevenueCustomFieldSelectProps> = ({
  outcome,
  onUpdateOutcome,
  isReadOnly,
}) => {
  const { data: allCustomFieldDefinitions, isLoading } =
    useAllUserCustomFieldDefinitionsQuery();

  const contactCustomFieldDefinitions: readonly CustomFieldDefinition[] =
    useMemo(
      () =>
        (allCustomFieldDefinitions ?? []).filter(
          (def) =>
            def.resource_type === CustomFieldResourceType.CONTACT &&
            def.field_type === CustomFieldType.Number,
        ),
      [allCustomFieldDefinitions],
    );

  const customFieldOptions: SimpleSelectOption<string>[] = useMemo(
    () =>
      contactCustomFieldDefinitions.map((def) => ({
        value: def.id,
        label: def.display_name,
      })),
    [contactCustomFieldDefinitions],
  );

  const committedCustomFieldId =
    outcome && isRevenueOutcomeDefinition(outcome)
      ? (outcome.custom_field_definition_id ?? null)
      : null;

  // If the committed field no longer exists in the eligible options (deleted
  // or no longer a contact number field), don't pass it to the select —
  // otherwise the select would render an empty/inconsistent label. Surface a
  // warning so the user can recover by picking a new field.
  const isCommittedFieldMissing =
    committedCustomFieldId !== null &&
    !isLoading &&
    !contactCustomFieldDefinitions.some(
      (def) => def.id === committedCustomFieldId,
    );

  const selectedCustomFieldId = isCommittedFieldMissing
    ? null
    : committedCustomFieldId;

  const handleCustomFieldSelect = (customFieldId: string): void => {
    onUpdateOutcome(buildRevenueOutcomeDefinition(customFieldId));
  };

  const hasNoEligibleFields =
    !isLoading && contactCustomFieldDefinitions.length === 0;

  return (
    <div>
      <Label size={Size.SMALL}>Contact custom field</Label>
      <p className="mt-1 text-xs text-gray-600">
        Select the contact custom field that contains the revenue amount.
      </p>
      <div className="mt-2">
        <SimpleSelect
          options={customFieldOptions}
          value={selectedCustomFieldId}
          onChange={handleCustomFieldSelect}
          placeholder="Select a contact custom field..."
          isDisabled={isReadOnly || isLoading}
        />
      </div>
      {hasNoEligibleFields ? (
        <p className="mt-2 text-xs text-destructive">
          No eligible contact number fields found. Create a contact custom field
          of type Number to use the Revenue outcome.
        </p>
      ) : null}
      {isCommittedFieldMissing ? (
        <p className="mt-2 text-xs text-destructive">
          The previously selected custom field is no longer available. Pick a
          new contact number field to continue using the Revenue outcome.
        </p>
      ) : null}
    </div>
  );
};
